import { c as _c } from "react/compiler-runtime";
import { FormSelect, toast } from '@systemeio/ui';
import { BadRequest } from '@systemeio/util';
import { updateUserAccountSettings } from 'modules/profile/account-settings/api/account-settings-api';
import AccountSettingsSkeleton from 'modules/profile/account-settings/components/account-settings-skeleton';
import { AccountSettingsLoco } from 'modules/profile/account-settings/constants/account-settings-loco';
import { useAccountSettings } from 'modules/profile/account-settings/hooks/use-account-settings';
import { useAccountSettingsTimezones } from 'modules/profile/account-settings/hooks/user-account-settings-timezones';
import { AccountSettingsEditableInterface, AccountSettingsErrorResponseContentInterface, AccountSettingsErrorsInterface, AccountSettingsInterface } from 'modules/profile/account-settings/types/account-settings-interface';
import { getFormatDataOptions, getFormatTimeOptions, getTimezonesDataOptions } from 'modules/profile/account-settings/utils/formats';
import SettingsLayout from 'modules/profile/components/settings-layout';
import { settingsMenuLocoKeys } from 'modules/profile/constants/settings-menu-loco-keys';
import { NextPage } from 'next';
import React, { useEffect, useState } from 'react';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props';
const defaultErrors: AccountSettingsErrorsInterface = {
  dateFormat: '',
  timeFormat: '',
  timezone: ''
};
const defaultValues: AccountSettingsEditableInterface = {
  dateFormat: undefined,
  timeFormat: undefined,
  timezone: ''
};
const AccountSettings: NextPage = () => {
  const $ = _c(33);
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  const [tempState, setTempState] = useState(defaultValues);
  const [errors, setErrors] = useState(defaultErrors);
  const [isFetching, setIsFetching] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [timezoneDataOptions, setTimezoneDataOptions] = useState(t0);
  const {
    userAccount,
    mutate
  } = useAccountSettings();
  const {
    data
  } = useAccountSettingsTimezones();
  const t1 = user?.timezone;
  let t2;
  if ($[1] !== t1) {
    t2 = getFormatDataOptions(t1);
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const dateFormatDataOptions = t2;
  const t3 = user?.timezone;
  let t4;
  if ($[3] !== t3) {
    t4 = getFormatTimeOptions(t3);
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const timeFormatDataOptions = t4;
  let t5;
  if ($[5] !== mutate || $[6] !== t || $[7] !== tempState) {
    t5 = async () => {
      ;
      try {
        const data_0 = {
          dateFormat: tempState.dateFormat,
          timeFormat: tempState.timeFormat,
          user: {
            timezone: tempState.timezone
          }
        };
        setIsFetching(true);
        const response = await updateUserAccountSettings(data_0);
        await mutate(response.data, false);
        setIsFetching(false);
        toast.success(t("global.changes_saved"));
        setErrors(defaultErrors);
      } catch (t6) {
        const e = t6;
        setIsFetching(false);
        if (e instanceof BadRequest) {
          const accountErrors = e.errors as unknown as AccountSettingsErrorResponseContentInterface;
          setErrors({
            dateFormat: accountErrors.fields.dateFormat?.join(""),
            timeFormat: accountErrors.fields.timeFormat?.join(""),
            timezone: accountErrors.fields.timezone?.join("")
          });
        } else {
          toast.error(t("global.error"));
        }
      }
    };
    $[5] = mutate;
    $[6] = t;
    $[7] = tempState;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const onChangeAccountSettings = t5;
  let t6;
  let t7;
  if ($[9] !== userAccount) {
    t6 = () => {
      if (userAccount) {
        setTempState({
          dateFormat: userAccount.dateFormat,
          timeFormat: userAccount.timeFormat,
          timezone: userAccount.user.timezone
        });
      }
    };
    t7 = [userAccount];
    $[9] = userAccount;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[12] !== data) {
    t8 = () => {
      if (data) {
        setTimezoneDataOptions(getTimezonesDataOptions(data.timezones));
      }
    };
    t9 = [data];
    $[12] = data;
    $[13] = t8;
    $[14] = t9;
  } else {
    t8 = $[13];
    t9 = $[14];
  }
  useEffect(t8, t9);
  let t10;
  if ($[15] !== t) {
    t10 = t(settingsMenuLocoKeys.account);
    $[15] = t;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10) {
    t11 = [{
      caption: t10,
      isStatic: true
    }];
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = !userAccount;
  let t13;
  if ($[19] !== dateFormatDataOptions || $[20] !== errors || $[21] !== t || $[22] !== tempState || $[23] !== timeFormatDataOptions || $[24] !== timezoneDataOptions || $[25] !== userAccount) {
    t13 = userAccount ? <div className="bg-background-secondary flex h-fit w-full flex-col gap-5 overflow-x-hidden rounded-md"><div className="flex flex-col gap-7 p-5 lg:gap-10"><div className="[&>*]:flex-1 flex flex-col justify-between gap-7 lg:flex-row lg:gap-10"><FormSelect placeholder={t("dashboard.user_settings.account.timezone_title")} data={timezoneDataOptions} label={t("dashboard.user_settings.account.timezone_title")} className="sm:min-w-0" required={true} value={tempState.timezone || undefined} onChange={value => value && setTempState(prev => ({
            ...prev,
            timezone: value
          }))} error={errors.timezone} withoutCloseIcon={true} /><FormSelect placeholder={t("dashboard.user_settings.account.date_format_title")} className="sm:min-w-0" data={dateFormatDataOptions} label={t("dashboard.user_settings.account.date_format_title")} required={true} value={tempState.dateFormat} onChange={value_0 => setTempState(prev_0 => ({
            ...prev_0,
            dateFormat: value_0
          }))} error={errors.dateFormat} withoutCloseIcon={true} /><FormSelect placeholder={t("dashboard.user_settings.account.time_format_title")} className="sm:min-w-0" data={timeFormatDataOptions} label={t("dashboard.user_settings.account.time_format_title")} required={true} value={tempState.timeFormat || undefined} onChange={value_1 => setTempState(prev_1 => ({
            ...prev_1,
            timeFormat: value_1
          }))} error={errors.timeFormat} withoutCloseIcon={true} /></div></div></div> : <AccountSettingsSkeleton />;
    $[19] = dateFormatDataOptions;
    $[20] = errors;
    $[21] = t;
    $[22] = tempState;
    $[23] = timeFormatDataOptions;
    $[24] = timezoneDataOptions;
    $[25] = userAccount;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== isFetching || $[28] !== onChangeAccountSettings || $[29] !== t11 || $[30] !== t12 || $[31] !== t13) {
    t14 = <SettingsLayout titles={t11} isFetching={isFetching} onSave={onChangeAccountSettings} disabled={t12}>{t13}</SettingsLayout>;
    $[27] = isFetching;
    $[28] = onChangeAccountSettings;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  return t14;
};
export const getServerSideProps = getBaseServerSideProps(AccountSettingsLoco, 'global.settings');
export default AccountSettings;